import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSpinner, 
  faCopy,
  faWandMagicSparkles,
  faUser,
  faBuilding,
  faDollarSign,
  faFileContract,
  faChartLine,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/api';
import './ReverseProposal.css';

/**
 * ReverseProposal Component
 * 
 * Creates compelling proposals for specific clients to pitch to brands.
 * Features:
 * - Client selection with search
 * - Brand information input
 * - AI-powered proposal generation
 * - Deliverables and pricing suggestions
 * - Professional proposal formatting
 */
const ReverseProposal = () => {
  const { user } = useAuth();
  
  // State management
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [brandInfo, setBrandInfo] = useState({
    brand_name: '',
    brand_description: '',
    campaign_objective: '',
    budget_target: '$1,000,000+'
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [emailSummary, setEmailSummary] = useState('');
  const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);

  // Load clients on component mount
  useEffect(() => {
    loadClients();
  }, []);

  /**
   * Load available clients from the API
   */
  const loadClients = async () => {
    try {
      setIsLoadingClients(true);
      const { tokens } = await fetchAuthSession();
      const response = await axios.get(API_ENDPOINTS.LEADS.GRID, {
        headers: {
          'Authorization': `Bearer ${tokens.accessToken.toString()}`
        }
      });
      
      if (response.data) {
        setClients(response.data);
      }
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setIsLoadingClients(false);
    }
  };

  /**
   * Filter clients based on search input
   */
  const filteredClients = clients.filter(client => {
    const searchLower = clientSearch.toLowerCase();
    const fullName = `${client.First_Legal_Name || ''} ${client.Last_Legal_Name || ''}`.toLowerCase();
    const stageName = (client.Stage_Name || '').toLowerCase();
    const clientId = (client.client_id || '').toLowerCase();
    
    return fullName.includes(searchLower) || 
           stageName.includes(searchLower) || 
           clientId.includes(searchLower);
  });

  /**
   * Handle client selection
   */
  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setClientSearch('');
    setProposal(null); // Clear previous proposal
  };

  /**
   * Handle brand information input changes
   */
  const handleBrandInfoChange = (field, value) => {
    setBrandInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  /**
   * Generate reverse proposal using AI
   */
  const generateProposal = async () => {
    if (!selectedClient || !brandInfo.brand_name.trim()) {
      toast.error('Please select a client and enter brand name');
      return;
    }

    try {
      setIsGenerating(true);
      
      const { tokens } = await fetchAuthSession();
      const response = await axios.post(API_ENDPOINTS.RFP.REVERSE_PROPOSAL, {
        client_id: selectedClient.client_id,
        brand_name: brandInfo.brand_name,
        brand_description: brandInfo.brand_description,
        campaign_objective: brandInfo.campaign_objective,
        budget_target: brandInfo.budget_target,
        user_id: user?.username || 'anonymous'
      }, {
        headers: {
          'Authorization': `Bearer ${tokens.accessToken.toString()}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.status === 'success') {
        setProposal(response.data);
        toast.success('Proposal generated successfully!');
      } else {
        toast.error('Failed to generate proposal');
      }
    } catch (error) {
      console.error('Error generating proposal:', error);
      toast.error('Error generating proposal: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * Generate email summary from the proposal
   */
  const generateEmailSummary = async () => {
    if (!proposal || !proposal.proposal) {
      toast.error('Please generate a proposal first');
      return;
    }

    try {
      setIsGeneratingEmail(true);
      
      const { tokens } = await fetchAuthSession();
      const response = await axios.post(API_ENDPOINTS.RFP.GENERATE_PROPOSAL_EMAIL, {
        rfp_data: {
          brand_name: brandInfo.brand_name,
          campaign_objective: brandInfo.campaign_objective,
          brand_description: brandInfo.brand_description,
          target_audience: 'General audience',
          budget_range: brandInfo.budget_target,
          campaign_duration: 'Not specified',
          deliverables: proposal.deliverables?.deliverables || [],
          requirements: proposal.proposal,
          deadline: 'Not specified',
          contact_name: 'Marketing Team',
          contact_email: 'marketing@brand.com',
          additional_notes: proposal.proposal
        },
        analysis: {
          campaign_summary: `Reverse proposal for ${proposal.client_name} to partner with ${brandInfo.brand_name}`,
          key_requirements: [`Partnership with ${proposal.client_name}`, `Budget target: ${brandInfo.budget_target}`],
          recommended_profile: `${proposal.client_name} - perfect fit for ${brandInfo.brand_name}`
        },
        proposed_clients: [selectedClient],
        user_id: user?.username || 'anonymous',
        formatting_instructions: 'Generate a professional, left-aligned email with no bold formatting, asterisks, or AI-style markdown. Use standard business email formatting with proper paragraphs and line breaks. This is a reverse proposal email pitching a specific creator to a brand.',
        reverse_proposal_data: {
          full_proposal: proposal.proposal,
          deliverables: proposal.deliverables,
          client_name: proposal.client_name,
          brand_name: brandInfo.brand_name
        }
      }, {
        headers: {
          'Authorization': `Bearer ${tokens.accessToken.toString()}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.status === 'success') {
        setEmailSummary(response.data.email_content);
        toast.success('Email summary generated successfully!');
      } else {
        toast.error('Failed to generate email summary');
      }
    } catch (error) {
      console.error('Error generating email summary:', error);
      toast.error('Error generating email summary: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsGeneratingEmail(false);
    }
  };

  /**
   * Format proposal text by removing markdown and converting to clean email format
   */
  const formatProposalText = (text) => {
    if (!text) return '';
    
    return text
      // Remove markdown headers (### -> blank line)
      .replace(/^#{1,6}\s+/gm, '')
      // Remove bold markdown (**text** -> text)
      .replace(/\*\*(.*?)\*\*/g, '$1')
      // Remove italic markdown (*text* -> text)
      .replace(/\*(.*?)\*/g, '$1')
      // Clean up multiple line breaks
      .replace(/\n{3,}/g, '\n\n')
      // Clean up any remaining markdown artifacts
      .replace(/^\s*-\s*\*\*/gm, '- ')
      .replace(/\*\*:/g, ':')
      // Ensure proper spacing after sections
      .replace(/---/g, '')
      .trim();
  };

  /**
   * Format email text by removing markdown and converting to clean email format
   */
  const formatEmailText = (text) => {
    if (!text) return '';
    
    return text
      // Remove markdown headers (### -> blank line)
      .replace(/^#{1,6}\s+/gm, '')
      // Remove bold markdown (**text** -> text)
      .replace(/\*\*(.*?)\*\*/g, '$1')
      // Remove italic markdown (*text* -> text)
      .replace(/\*(.*?)\*/g, '$1')
      // Clean up multiple line breaks
      .replace(/\n{3,}/g, '\n\n')
      // Clean up any remaining markdown artifacts
      .replace(/^\s*-\s*\*\*/gm, '- ')
      .replace(/\*\*:/g, ':')
      // Ensure proper spacing after sections
      .replace(/---/g, '')
      .trim();
  };

  /**
   * Copy content to clipboard
   */
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  /**
   * Format currency for display
   */
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  /**
   * Get client display name
   */
  const getClientDisplayName = (client) => {
    return client.Stage_Name || `${client.First_Legal_Name || ''} ${client.Last_Legal_Name || ''}`.trim();
  };

  /**
   * Get client follower count summary
   */
  const getFollowerSummary = (client) => {
    const ig = client.IG_Followers || 0;
    const tt = client.TT_Followers || 0;
    const yt = client.YT_Followers || 0;
    const snap = client.Snap_Followers || 0;
    const total = ig + tt + yt + snap;
    
    return `${total.toLocaleString()} total followers`;
  };

  return (
    <div className="reverse-proposal-container">
      <div className="reverse-proposal-header">
        <div className="simple-header">
          <h2>Find Brands</h2>
          <p>Create compelling proposals for your top creators to pitch to major brands</p>
        </div>
      </div>

      <div className="reverse-proposal-content">
        {/* Client Selection Section */}
        <div className="reverse-proposal-section reverse-proposal-client-selection">
          <h2>
            <FontAwesomeIcon icon={faUser} />
            Select Creator
          </h2>
          
          <div className="reverse-proposal-client-search-container">
            <input
              type="text"
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              placeholder="Search for a creator..."
              className="reverse-proposal-client-search-input"
            />
            
            {clientSearch && (
              <div className="reverse-proposal-client-search-results">
                {isLoadingClients ? (
                  <div className="reverse-proposal-loading-item">
                    <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                  </div>
                ) : (
                  filteredClients.slice(0, 10).map(client => (
                    <div
                      key={client.client_id}
                      className="reverse-proposal-client-search-item"
                      onClick={() => handleClientSelect(client)}
                    >
                      <div className="reverse-proposal-client-name">{getClientDisplayName(client)}</div>
                      <div className="reverse-proposal-client-details">
                        {client.client_id} • {getFollowerSummary(client)}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {selectedClient && (
            <div className="creator-card selected">
              <div className="creator-card-header">
                <div className="creator-card-main-info">
                  <h4 className="creator-card-name">{getClientDisplayName(selectedClient)}</h4>
                </div>
                <div className="creator-card-total-reach">
                  {getFollowerSummary(selectedClient)}
                </div>
              </div>
              
              <div className="creator-card-stats-grid">
                <div className="creator-stat-item">
                  <div className="creator-stat-platform">Instagram</div>
                  <div className="creator-stat-value">{(selectedClient.IG_Followers || 0).toLocaleString()}</div>
                </div>
                <div className="creator-stat-item">
                  <div className="creator-stat-platform">TikTok</div>
                  <div className="creator-stat-value">{(selectedClient.TT_Followers || 0).toLocaleString()}</div>
                </div>
              </div>
              
              <div className="creator-card-status-badges">
                {selectedClient.monetized && <span className="creator-status-badge monetized">Monetized</span>}
                {selectedClient.signed && <span className="creator-status-badge signed">Signed</span>}
                {selectedClient.verified && <span className="creator-status-badge verified">Verified</span>}
                {selectedClient.star_talent && <span className="creator-status-badge star">Star Talent</span>}
              </div>
            </div>
          )}
        </div>

        {/* Brand Information Section */}
        <div className="reverse-proposal-section reverse-proposal-brand-info">
          <h2>
            <FontAwesomeIcon icon={faBuilding} />
            Brand Information
          </h2>
          
          <div className="reverse-proposal-brand-form">
            <div className="reverse-proposal-form-group">
              <label>Brand Name *</label>
              <input
                type="text"
                value={brandInfo.brand_name}
                onChange={(e) => handleBrandInfoChange('brand_name', e.target.value)}
                placeholder="e.g., Coca-Cola, Nike, Apple"
                required
              />
            </div>
            
            <div className="reverse-proposal-form-group">
              <label>Brand Description</label>
              <textarea
                value={brandInfo.brand_description}
                onChange={(e) => handleBrandInfoChange('brand_description', e.target.value)}
                placeholder="Brief description of the brand and its values..."
                rows="3"
              />
            </div>
            
            <div className="reverse-proposal-form-group">
              <label>Campaign Objective</label>
              <input
                type="text"
                value={brandInfo.campaign_objective}
                onChange={(e) => handleBrandInfoChange('campaign_objective', e.target.value)}
                placeholder="e.g., Brand awareness, Product launch, Holiday campaign"
              />
            </div>
            
            <div className="reverse-proposal-form-group">
              <label>Budget Target</label>
              <select
                value={brandInfo.budget_target}
                onChange={(e) => handleBrandInfoChange('budget_target', e.target.value)}
              >
                <option value="$500,000+">$500,000+</option>
                <option value="$1,000,000+">$1,000,000+</option>
                <option value="$2,000,000+">$2,000,000+</option>
                <option value="$5,000,000+">$5,000,000+</option>
              </select>
            </div>
          </div>
        </div>

        {/* Generate Button */}
        <div className="reverse-proposal-generate-section">
          <button
            onClick={generateProposal}
            disabled={isGenerating || !selectedClient || !brandInfo.brand_name.trim()}
            className="reverse-proposal-generate-btn"
          >
            {isGenerating ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> Generating Million-Dollar Proposal...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faWandMagicSparkles} /> Generate Proposal
              </>
            )}
          </button>
        </div>

        {/* Results Section */}
        {proposal && (
          <div className="reverse-proposal-proposal-results">
            <div className="reverse-proposal-results-header">
              <h2>
                <FontAwesomeIcon icon={faChartLine} />
                Proposal for {proposal.client_name} × {brandInfo.brand_name}
              </h2>
              <button
                onClick={() => copyToClipboard(formatProposalText(proposal.proposal))}
                className="reverse-proposal-copy-btn"
              >
                <FontAwesomeIcon icon={faCopy} /> Copy Proposal
              </button>
            </div>

            {/* Campaign Package */}
            {proposal.deliverables && (
              <div className="campaign-package-section">
                <h3 className="campaign-package-title">
                  <FontAwesomeIcon icon={faDollarSign} />
                  Proposed Campaign Package
                </h3>
                
                <div className="campaign-deliverables-grid">
                  {proposal.deliverables.deliverables?.map((deliverable, index) => (
                    <div key={index} className="campaign-deliverable-card">
                      <div className="campaign-deliverable-header">
                        <div className="campaign-deliverable-type">{deliverable.type}</div>
                        <div className="campaign-deliverable-quantity">×{deliverable.quantity}</div>
                      </div>
                      <div className="campaign-deliverable-description">
                        {deliverable.description}
                      </div>
                      <div className="campaign-deliverable-pricing">
                        <span className="campaign-unit-price">{formatCurrency(deliverable.price_per_unit)} each</span>
                        <span className="campaign-total-price">{formatCurrency(deliverable.total_price)}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="campaign-pricing-summary">
                  <div className="campaign-pricing-breakdown">
                    <div className="campaign-pricing-item">
                      <span>Campaign Content</span>
                      <strong>{formatCurrency(proposal.deliverables.total_campaign_value || 0)}</strong>
                    </div>
                    <div className="campaign-pricing-item">
                      <span>Usage Rights</span>
                      <strong>{formatCurrency(proposal.deliverables.usage_rights_fee || 0)}</strong>
                    </div>
                    <div className="campaign-pricing-item">
                      <span>Exclusivity Premium</span>
                      <strong>{formatCurrency(proposal.deliverables.exclusivity_premium || 0)}</strong>
                    </div>
                    <div className="campaign-pricing-item">
                      <span>Performance Bonus Potential</span>
                      <strong>{formatCurrency(proposal.deliverables.performance_bonus_potential || 0)}</strong>
                    </div>
                  </div>
                  <div className="campaign-total-value">
                    <span>Total Campaign Value</span>
                    <strong>{formatCurrency(proposal.deliverables.grand_total || 0)}</strong>
                  </div>
                </div>
              </div>
            )}

            {/* Full Proposal */}
            <div className="reverse-proposal-full-proposal">
              <h3>Complete Proposal Document</h3>
              <div className="reverse-proposal-email-content">
                <div className="reverse-proposal-email-text">{formatProposalText(proposal.proposal)}</div>
              </div>
              <button
                onClick={() => copyToClipboard(formatProposalText(proposal.proposal))}
                className="reverse-proposal-copy-full-btn"
              >
                <FontAwesomeIcon icon={faCopy} /> Copy Full Proposal
              </button>
            </div>

            {/* Email Summary Section */}
            <div className="reverse-proposal-email-section">
              <button
                onClick={generateEmailSummary}
                disabled={isGeneratingEmail}
                className="reverse-proposal-generate-email-btn"
              >
                {isGeneratingEmail ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Generating Email Summary...
                  </>
                ) : (
                  <>
                    Generate Email Summary <FontAwesomeIcon icon={faArrowRight} />
                  </>
                )}
              </button>

              {emailSummary && (
                <div className="reverse-proposal-email-result">
                  <div className="reverse-proposal-email-header">
                    <h3>Email Summary</h3>
                    <button
                      onClick={() => copyToClipboard(formatEmailText(emailSummary))}
                      className="reverse-proposal-copy-btn"
                    >
                      <FontAwesomeIcon icon={faCopy} /> Copy Email
                    </button>
                  </div>
                  <div className="reverse-proposal-email-content">
                    <div className="reverse-proposal-email-text">{formatEmailText(emailSummary)}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReverseProposal; 